import { Badge, PageContainer } from '@ccp/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { homePathFor } from '../app/shell/areas.js';
import { meQueryOptions } from '../session/use-current-user.js';

interface IndexSearch {
  /** The permission an area wanted, when somebody arrived here because they were refused one. */
  readonly denied?: string;
}

/**
 * The front door: it takes people to their own area.
 *
 * It is also where an unauthorised deep link lands, with the permission it needed in the address —
 * which is why a `denied` arrival does **not** redirect: bouncing somebody straight back to the
 * area they just came from would be a loop, and they would never read the explanation.
 */
export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): IndexSearch => ({
    denied: typeof search.denied === 'string' ? search.denied : undefined,
  }),
  async beforeLoad({ context, search }) {
    if (search.denied !== undefined) {
      return;
    }
    const home = homePathFor(await context.queryClient.ensureQueryData(meQueryOptions()));
    if (home !== undefined) {
      throw redirect({ to: home });
    }
  },
  component: Home,
});

function Home() {
  const { t } = useTranslation();
  const { denied } = Route.useSearch();

  return (
    <PageContainer width="medium" className="items-start">
      {denied === undefined ? null : <Badge tone="warning">{t('state.deniedBadge')}</Badge>}
      <h1 className="text-(length:--font-size-heading) font-semibold text-(--color-text-primary)">
        {denied === undefined ? t('product') : t('state.deniedTitle')}
      </h1>
      <p className="text-(length:--font-size-body) text-(--color-text-secondary)">
        {denied === undefined
          ? t('state.noAreaBody')
          : t('state.deniedBody', { permission: denied })}
      </p>
    </PageContainer>
  );
}
